import type { MouseEventHandler, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import type { RectCords } from 'folds';
import { Box, Text, Chip, Menu, config, MenuItem, color } from 'folds';
import { PopOut } from '$components/overlay-stack';
import { CaretDown, sizedIcon } from '$components/icons/phosphor';
import FocusTrap from 'focus-trap-react';
import type { SecretStorageKeyContent } from '$types/matrix/accountData';
import type { CryptoBackend } from '$types/matrix-sdk';
import { storePrivateKey } from '$client/secretStorageKeys';
import { stopPropagation } from '$utils/keyboard';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { useRefreshDeviceVerificationStatus } from '$hooks/useDeviceVerificationStatus';
import { AsyncStatus, useAsyncCallback } from '$hooks/useAsyncCallback';
import { AsyncError } from '$components/AsyncError';
import { SettingTile } from './setting-tile';
import { SecretStorageKeyMethod, SecretStorageKeyPrompt } from './SecretStorage';

type ManualVerificationMethodSwitcherProps = {
  value: SecretStorageKeyMethod;
  onChange: (value: SecretStorageKeyMethod) => void;
};
function ManualVerificationMethodSwitcher({
  value,
  onChange,
}: ManualVerificationMethodSwitcherProps) {
  const [menuCords, setMenuCords] = useState<RectCords>();

  const handleMenu: MouseEventHandler<HTMLButtonElement> = (evt) => {
    setMenuCords(evt.currentTarget.getBoundingClientRect());
  };

  const handleSelect = (method: SecretStorageKeyMethod) => {
    setMenuCords(undefined);
    onChange(method);
  };

  return (
    <>
      <Chip
        type="button"
        variant="Secondary"
        fill="Soft"
        radii="Pill"
        before={sizedIcon(CaretDown, '100')}
        onClick={handleMenu}
      >
        <Text as="span" size="B300">
          {value === SecretStorageKeyMethod.RecoveryPassphrase && 'Recovery Passphrase'}
          {value === SecretStorageKeyMethod.RecoveryKey && 'Recovery Key'}
        </Text>
      </Chip>
      <PopOut
        anchor={menuCords}
        offset={5}
        position="Bottom"
        align="End"
        content={
          <FocusTrap
            focusTrapOptions={{
              initialFocus: false,
              onDeactivate: () => setMenuCords(undefined),
              clickOutsideDeactivates: true,
              isKeyForward: (evt: KeyboardEvent) =>
                evt.key === 'ArrowDown' || evt.key === 'ArrowRight',
              isKeyBackward: (evt: KeyboardEvent) =>
                evt.key === 'ArrowUp' || evt.key === 'ArrowLeft',
              escapeDeactivates: stopPropagation,
            }}
          >
            <Menu>
              <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
                <MenuItem
                  size="300"
                  variant="Surface"
                  aria-selected={value === SecretStorageKeyMethod.RecoveryPassphrase}
                  radii="300"
                  onClick={() => handleSelect(SecretStorageKeyMethod.RecoveryPassphrase)}
                >
                  <Box grow="Yes">
                    <Text size="T300">Recovery Passphrase</Text>
                  </Box>
                </MenuItem>
                <MenuItem
                  size="300"
                  variant="Surface"
                  aria-selected={value === SecretStorageKeyMethod.RecoveryKey}
                  radii="300"
                  onClick={() => handleSelect(SecretStorageKeyMethod.RecoveryKey)}
                >
                  <Box grow="Yes">
                    <Text size="T300">Recovery Key</Text>
                  </Box>
                </MenuItem>
              </Box>
            </Menu>
          </FocusTrap>
        }
      />
    </>
  );
}

type ManualVerificationTileProps = {
  secretStorageKeyId: string;
  secretStorageKeyContent: SecretStorageKeyContent;
  options?: ReactNode;
};
export function ManualVerificationTile({
  secretStorageKeyId,
  secretStorageKeyContent,
  options,
}: ManualVerificationTileProps) {
  const mx = useMatrixClient();

  const hasPassphrase = !!secretStorageKeyContent.passphrase;
  const [method, setMethod] = useState(
    hasPassphrase ? SecretStorageKeyMethod.RecoveryPassphrase : SecretStorageKeyMethod.RecoveryKey
  );

  const refreshVerificationStatus = useRefreshDeviceVerificationStatus();

  const verifyAndRestoreBackup = useCallback(
    async (recoveryKey: Uint8Array) => {
      const crypto = mx.getCrypto() as CryptoBackend | undefined;
      if (!crypto) {
        throw new Error('Unexpected Error! Crypto object not found.');
      }

      storePrivateKey(secretStorageKeyId, recoveryKey);

      await crypto.processDeviceLists({ changed: [mx.getSafeUserId()] });
      await crypto.bootstrapCrossSigning({});
      await crypto.bootstrapSecretStorage({});

      await crypto.loadSessionBackupPrivateKeyFromSecretStorage();

      refreshVerificationStatus();
    },
    [mx, secretStorageKeyId, refreshVerificationStatus]
  );

  const [verifyState, handleDecodedRecoveryKey] = useAsyncCallback<void, Error, [Uint8Array]>(
    verifyAndRestoreBackup
  );
  const verifying = verifyState.status === AsyncStatus.Loading;

  return (
    <Box direction="Column" gap="200">
      <SettingTile
        title="Verify Manually"
        description={hasPassphrase ? 'Select a verification method.' : 'Provide recovery key.'}
        after={
          <Box alignItems="Center" gap="200">
            {hasPassphrase && (
              <ManualVerificationMethodSwitcher value={method} onChange={setMethod} />
            )}
            {options}
          </Box>
        }
      />
      {verifyState.status === AsyncStatus.Success ? (
        <Text size="T200" style={{ color: color.Success.Main }}>
          <b>Device verified!</b>
        </Text>
      ) : (
        <Box direction="Column" gap="100">
          <SecretStorageKeyPrompt
            method={method}
            processing={verifying}
            keyContent={secretStorageKeyContent}
            onDecodedRecoveryKey={handleDecodedRecoveryKey}
          />
          <AsyncError state={verifyState} bold />
        </Box>
      )}
    </Box>
  );
}
